import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";

import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import Loading from "../../components/Loading";

import queryString from "../../functions/queryString";
import {
    getBusiness,
    getBusinessUserLocationStats,
} from "../../api/routes";

import "@neshan-maps-platform/mapbox-gl/dist/NeshanMapboxGl.css";

import {
    Box,
    Button,
    Card,
    Divider,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { alpha } from "@mui/material/styles";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import RadarRoundedIcon from "@mui/icons-material/RadarRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import NearMeRoundedIcon from "@mui/icons-material/NearMeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

import SelectedBusinessCard from "./SelectedBusinessCard";
import MapPickerCard from "./MapPickerCard";
import BusinessCard from "./BusinessCard";
import EmptyBusinessState from "./EmptyBusinessState";
import StatsCard from "./StatsCard";

const DATE_RANGE_PRESETS = [
    { key: "last_1_week", label: "یک هفته گذشته", type: "days", value: 7 },
    { key: "last_2_weeks", label: "دو هفته گذشته", type: "days", value: 14 },
    { key: "last_1_month", label: "یک ماه گذشته", type: "months", value: 1 },
    { key: "last_2_months", label: "دو ماه گذشته", type: "months", value: 2 },
    { key: "last_3_months", label: "سه ماه گذشته", type: "months", value: 3 },
    { key: "last_6_months", label: "شش ماه گذشته", type: "months", value: 6 },
];

const DEFAULT_DATE_RANGE_PRESET_KEY = "last_1_month";

const COLORS = {
    primary: "#0284c7",
    primaryLight: "#0ea5e9",
    primaryDark: "#0369a1",
    text: "#0f172a",
    muted: "#64748b",
    danger: "#ef4444",
};

const formatDateForInput = (date) => {
    const localDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
    );

    return localDate.toISOString().split("T")[0];
};

const getPresetDateRange = (preset) => {
    const to = new Date();
    const from = new Date();

    if (preset.type === "days") {
        from.setDate(from.getDate() - preset.value);
    }

    if (preset.type === "months") {
        from.setMonth(from.getMonth() - preset.value);
    }

    return {
        from_date: formatDateForInput(from),
        to_date: formatDateForInput(to),
    };
};

const getDefaultDateRange = () => {
    const preset = DATE_RANGE_PRESETS.find(
        (item) => item.key === DEFAULT_DATE_RANGE_PRESET_KEY
    );

    return getPresetDateRange(preset);
};

const getLat = (item) =>
    item?.lat ?? item?.latitude ?? null;

const getLong = (item) =>
    item?.long ?? item?.longitude ?? item?.lng ?? null;

const hasCoordinate = (value) =>
    value !== null &&
    value !== undefined &&
    value !== "";

const formatDistance = (value) =>
    value !== null && value !== undefined
        ? `${value} km`
        : "-";

export const panelCardSx = {
    borderRadius: 4,
    border: "1px solid rgba(226, 232, 240, 0.95)",
    background: "#fff",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.06)",
};

export const fieldSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 3,
        backgroundColor: "#fff",
        transition: "all .2s ease",

        "& fieldset": {
            borderColor: "rgba(100, 116, 139, 0.26)",
        },

        "&:hover fieldset": {
            borderColor: "rgba(14, 165, 233, 0.55)",
        },

        "&.Mui-focused fieldset": {
            borderColor: COLORS.primaryLight,
            borderWidth: 1.5,
        },
    },

    "& .MuiInputBase-input": {
        color: COLORS.text,
        fontWeight: 700,
        fontSize: 14,
    },

    "& .MuiInputBase-input::placeholder": {
        color: "#94a3b8",
        opacity: 1,
        fontWeight: 500,
    },

    "& .MuiInputLabel-root": {
        color: "#475569",
        fontWeight: 800,
    },

    "& .MuiInputLabel-root.Mui-focused": {
        color: COLORS.primary,
    },

    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
        color: COLORS.muted,
    },
};

export const primaryButtonSx = {
    height: 40,
    borderRadius: 3,
    fontWeight: 900,
    color: "#fff",
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryLight} 100%)`,
    boxShadow: "0 14px 28px rgba(14, 165, 233, 0.22)",

    "&:hover": {
        background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 100%)`,
        boxShadow: "0 16px 34px rgba(14, 165, 233, 0.30)",
    },

    "&.Mui-disabled": {
        background: "rgba(148, 163, 184, 0.16)",
        color: "#94a3b8",
        boxShadow: "none",
    },
};

const sectionIconSx = (color = COLORS.primary) => ({
    width: 42,
    height: 42,
    borderRadius: 2.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    bgcolor: alpha(color, 0.1),
    color,
});

const formatPdfValue = (value, suffix = "") => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    return `${value}${suffix}`;
};

const drawRoundedRect = (
    ctx,
    x,
    y,
    width,
    height,
    radius
) => {
    const r = Math.min(
        radius,
        width / 2,
        height / 2
    );

    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(
        x + width,
        y,
        x + width,
        y + r
    );
    ctx.lineTo(
        x + width,
        y + height - r
    );
    ctx.quadraticCurveTo(
        x + width,
        y + height,
        x + width - r,
        y + height
    );
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(
        x,
        y + height,
        x,
        y + height - r
    );
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(
        x,
        y,
        x + r,
        y
    );
    ctx.closePath();
};

const fitText = (ctx, text, maxWidth) => {
    const value = String(text || "");

    if (ctx.measureText(value).width <= maxWidth) {
        return value;
    }

    let result = value;

    while (
        result.length > 1 &&
        ctx.measureText(`${result}…`).width >
            maxWidth
    ) {
        result = result.slice(0, -1);
    }

    return `${result}…`;
};

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
    });

export default function NearbyVisitStats() {
    const [loading, setLoading] =
        useState(false);

    const [loadingStats, setLoadingStats] =
        useState(false);

    const [exportingPdf, setExportingPdf] =
        useState(false);

    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] =
        useState(false);

    const [business, setBusiness] =
        useState([]);

    const [
        selectedBusiness,
        setSelectedBusiness,
    ] = useState(null);

    const [mapPosition, setMapPosition] =
        useState(null);

    const [
        locationSource,
        setLocationSource,
    ] = useState("business");

    const [stats, setStats] = useState(null);

    const [
        selectedRangePreset,
        setSelectedRangePreset,
    ] = useState(
        DEFAULT_DATE_RANGE_PRESET_KEY
    );

    const [searchInfo, setSearchInfo] =
        useState(() => ({
            name: "",
            radius: "",
            ...getDefaultDateRange(),
        }));

    useEffect(() => {
        const timeout = setTimeout(
            () => {
                setLoading(true);

                const query = {
                    name:
                        searchInfo.name ||
                        null,
                    status: null,
                    per_page: 4,
                };

                getBusiness(
                    queryString(query),
                    page !== 0,
                    (isOk, data) => {
                        setLoading(false);

                        if (!isOk) {
                            toast.error(
                                data.message
                            );
                            return;
                        }

                        setBusiness((prev) =>
                            page !== 0
                                ? [
                                      ...prev,
                                      ...data.data,
                                  ]
                                : data.data
                        );

                        setHasMore(
                            data.meta
                                .next_cursor !==
                                null
                        );
                    }
                );
            },
            searchInfo.name ? 700 : 0
        );

        return () =>
            clearTimeout(timeout);
    }, [searchInfo.name, page]);

    const handleChange = (event) => {
        const { name, value } =
            event.target;

        if (name === "name") {
            setPage(0);
        }

        if (
            name === "from_date" ||
            name === "to_date"
        ) {
            setSelectedRangePreset("");
        }

        setSearchInfo((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDateRangePreset = (
        preset
    ) => {
        setSelectedRangePreset(
            preset.key
        );

        setSearchInfo((prev) => ({
            ...prev,
            ...getPresetDateRange(preset),
        }));
    };

    const clearDateRange = () => {
        setSelectedRangePreset("");

        setSearchInfo((prev) => ({
            ...prev,
            from_date: "",
            to_date: "",
        }));
    };

    const fetchMoreData = () => {
        if (!loading && hasMore) {
            setPage(
                (prev) => prev + 1
            );
        }
    };

    const fetchStats = (
        position = null
    ) => {
        let lat = null;
        let long = null;

        if (
            hasCoordinate(
                position?.lat
            ) &&
            hasCoordinate(
                position?.long
            )
        ) {
            lat = position.lat;
            long = position.long;
        } else if (
            locationSource === "map" &&
            mapPosition
        ) {
            lat = mapPosition.lat;
            long = mapPosition.long;
        } else if (selectedBusiness) {
            lat =
                getLat(
                    selectedBusiness
                );

            long =
                getLong(
                    selectedBusiness
                );
        }

        if (
            !hasCoordinate(lat) ||
            !hasCoordinate(long)
        ) {
            toast.error(
                "ابتدا یک بیزینس انتخاب کنید یا یک نقطه روی نقشه مشخص کنید."
            );
            return;
        }

        setLoadingStats(true);

        const query = {
            lat,
            long,
            radius:
                searchInfo.radius ||
                null,
            from_date:
                searchInfo.from_date ||
                null,
            to_date:
                searchInfo.to_date ||
                null,
        };

        getBusinessUserLocationStats(
            queryString(query),
            (isOk, data) => {
                setLoadingStats(false);

                if (!isOk) {
                    setStats(null);
                    toast.error(
                        data.message
                    );
                    return;
                }

                setStats(data);
            }
        );
    };

    const handleSelectBusiness = (
        item
    ) => {
        const lat = getLat(item);
        const long = getLong(item);

        if (
            !hasCoordinate(lat) ||
            !hasCoordinate(long)
        ) {
            toast.error(
                "برای این بیزینس مختصات جغرافیایی ثبت نشده است."
            );
            return;
        }

        const position = {
            lat: Number(lat),
            long: Number(long),
        };

        setSelectedBusiness(item);
        setMapPosition(position);
        setLocationSource("business");
        setStats(null);

        fetchStats(position);
    };

    const handleMapPositionChange = (
        position
    ) => {
        setMapPosition(position);
        setLocationSource("map");
        setSelectedBusiness(null);
        setStats(null);
    };

    const handleExportPdf = async () => {
        if (
            !stats ||
            !selectedBusiness ||
            exportingPdf
        ) {
            return;
        }

        try {
            setExportingPdf(true);

            if (document.fonts?.ready) {
                await document.fonts.ready;
            }

            const logo =
                await loadImage(
                    "/images/logo.png"
                );

            const baseWidth = 559;
            const baseHeight = 794;
            const scale = 2;

            const canvas =
                document.createElement(
                    "canvas"
                );

            canvas.width =
                baseWidth * scale;
            canvas.height =
                baseHeight * scale;

            const ctx =
                canvas.getContext("2d");

            if (!ctx) {
                throw new Error(
                    "Canvas is not available"
                );
            }

            ctx.scale(scale, scale);
            ctx.textBaseline = "middle";

            ctx.fillStyle = "#fff";
            ctx.fillRect(
                0,
                0,
                baseWidth,
                baseHeight
            );

            const sheetX = 38;
            const sheetY = 38;
            const sheetW =
                baseWidth - 76;
            const sheetH =
                baseHeight - 72;

            drawRoundedRect(
                ctx,
                sheetX,
                sheetY,
                sheetW,
                sheetH,
                24
            );

            ctx.fillStyle = "#fff";
            ctx.fill();

            ctx.lineWidth = 1;
            ctx.strokeStyle =
                "#eadfd4";
            ctx.stroke();

            const headerH = 108;

            ctx.save();

            drawRoundedRect(
                ctx,
                sheetX,
                sheetY,
                sheetW,
                headerH,
                24
            );

            ctx.clip();

            const gradient =
                ctx.createLinearGradient(
                    0,
                    sheetY,
                    0,
                    sheetY +
                        headerH
                );

            gradient.addColorStop(
                0,
                "#fff8f1"
            );

            gradient.addColorStop(
                1,
                "#ffffff"
            );

            ctx.fillStyle = gradient;

            ctx.fillRect(
                sheetX,
                sheetY,
                sheetW,
                headerH
            );

            ctx.restore();

            ctx.strokeStyle =
                "#efe7df";

            ctx.beginPath();
            ctx.moveTo(
                sheetX,
                sheetY + headerH
            );
            ctx.lineTo(
                sheetX + sheetW,
                sheetY + headerH
            );
            ctx.stroke();

            const logoMaxWidth = 115;
            const logoMaxHeight = 68;

            const imageRatio =
                logo.width /
                logo.height;

            let logoWidth =
                logoMaxWidth;

            let logoHeight =
                logoWidth /
                imageRatio;

            if (
                logoHeight >
                logoMaxHeight
            ) {
                logoHeight =
                    logoMaxHeight;

                logoWidth =
                    logoHeight *
                    imageRatio;
            }

            const logoX =
                sheetX +
                sheetW -
                logoWidth -
                26;

            const logoY =
                sheetY +
                (headerH -
                    logoHeight) /
                    2;

            ctx.drawImage(
                logo,
                logoX,
                logoY,
                logoWidth,
                logoHeight
            );

            ctx.direction = "ltr";
            ctx.textAlign = "left";
            ctx.fillStyle =
                "#8a6f61";
            ctx.font =
                "700 13px Tahoma, Arial, sans-serif";

            ctx.fillText(
                "cafeberi.ir",
                sheetX + 28,
                sheetY +
                    headerH / 2
            );

            ctx.direction = "rtl";
            ctx.textAlign = "right";

            ctx.fillStyle =
                "#9a8579";
            ctx.font =
                "700 12px Tahoma, Arial, sans-serif";

            ctx.fillText(
                "آمار بازدید کافی‌شاپ",
                sheetX +
                    sheetW -
                    28,
                sheetY +
                    headerH +
                    28
            );

            ctx.fillStyle =
                "#2f211b";

            ctx.font =
                "900 20px Tahoma, Arial, sans-serif";

            const shopName =
                fitText(
                    ctx,
                    selectedBusiness.name ||
                        "کافی‌شاپ",
                    sheetW - 56
                );

            ctx.fillText(
                shopName,
                sheetX +
                    sheetW -
                    28,
                sheetY +
                    headerH +
                    58
            );

            const cards = [
                [
                    "کل بازدیدها",
                    formatPdfValue(
                        stats.total_visits
                    ),
                ],
                [
                    "بازدید مؤثر ۲ ساعته",
                    formatPdfValue(
                        stats.effective_visits_2h
                    ),
                ],
                [
                    "کاربران یکتا",
                    formatPdfValue(
                        stats.unique_users
                    ),
                ],
                [
                    "نزدیک‌ترین فاصله",
                    formatPdfValue(
                        stats.nearest_distance,
                        " km"
                    ),
                ],
                [
                    "میانگین فاصله",
                    formatPdfValue(
                        stats.avg_distance,
                        " km"
                    ),
                ],
                [
                    "دورترین فاصله",
                    formatPdfValue(
                        stats.farthest_distance,
                        " km"
                    ),
                ],
            ];

            const contentX =
                sheetX + 28;

            const contentY =
                sheetY +
                headerH +
                92;

            const contentW =
                sheetW - 56;

            const gap = 14;

            const cardW =
                (contentW - gap) /
                2;

            const cardH = 145;

            cards.forEach(
                (
                    [title, value],
                    index
                ) => {
                    const col =
                        index % 2;

                    const row =
                        Math.floor(
                            index / 2
                        );

                    const x =
                        contentX +
                        col *
                            (cardW +
                                gap);

                    const y =
                        contentY +
                        row *
                            (cardH +
                                gap);

                    drawRoundedRect(
                        ctx,
                        x,
                        y,
                        cardW,
                        cardH,
                        18
                    );

                    ctx.fillStyle =
                        "#fffdfb";
                    ctx.fill();

                    ctx.strokeStyle =
                        "#eadfd4";
                    ctx.stroke();

                    ctx.direction =
                        "rtl";
                    ctx.textAlign =
                        "center";

                    ctx.fillStyle =
                        "#8a6f61";
                    ctx.font =
                        "700 12px Tahoma, Arial, sans-serif";

                    ctx.fillText(
                        title,
                        x +
                            cardW /
                                2,
                        y + 50
                    );

                    ctx.direction =
                        "ltr";

                    ctx.fillStyle =
                        "#3f2b21";

                    ctx.font =
                        "900 24px Tahoma, Arial, sans-serif";

                    ctx.fillText(
                        String(value),
                        x +
                            cardW /
                                2,
                        y + 91
                    );
                }
            );

            const imageData =
                canvas.toDataURL(
                    "image/png",
                    1
                );

            const pdf = new jsPDF({
                orientation:
                    "portrait",
                unit: "mm",
                format: "a5",
                compress: true,
            });

            pdf.addImage(
                imageData,
                "PNG",
                0,
                0,
                148,
                210,
                undefined,
                "FAST"
            );

            const safeName = (
                selectedBusiness.name ||
                "coffee-shop"
            )
                .replace(
                    /[\/:*?"<>|]/g,
                    "-"
                )
                .trim();

            pdf.save(
                `cafeberi-${safeName}-stats.pdf`
            );
        } catch (error) {
            console.error(error);

            toast.error(
                "ساخت فایل PDF با خطا مواجه شد."
            );
        } finally {
            setExportingPdf(false);
        }
    };

    const statsItems = stats
        ? [
              {
                  title: "کل بازدیدها",
                  value:
                      stats.total_visits,
                  icon: (
                      <TrendingUpRoundedIcon />
                  ),
                  color: "#0284c7",
              },
              {
                  title:
                      "بازدید مؤثر ۲ ساعته",
                  value:
                      stats.effective_visits_2h,
                  icon: (
                      <AccessTimeRoundedIcon />
                  ),
                  color: "#7c3aed",
              },
              {
                  title:
                      "کاربران یکتا",
                  value:
                      stats.unique_users,
                  icon: (
                      <PeopleAltRoundedIcon />
                  ),
                  color: "#0f766e",
              },
              {
                  title:
                      "نزدیک‌ترین فاصله",
                  value: formatDistance(
                      stats.nearest_distance
                  ),
                  icon: (
                      <NearMeRoundedIcon />
                  ),
                  color: "#16a34a",
              },
              {
                  title:
                      "میانگین فاصله",
                  value: formatDistance(
                      stats.avg_distance
                  ),
                  icon: (
                      <RadarRoundedIcon />
                  ),
                  color: "#ea580c",
              },
              {
                  title:
                      "دورترین فاصله",
                  value: formatDistance(
                      stats.farthest_distance
                  ),
                  icon: (
                      <LocationOnRoundedIcon />
                  ),
                  color: "#dc2626",
              },
          ]
        : [];

    return (
        <DashboardLayout>
            <Loading
                loading={
                    loading ||
                    loadingStats
                }
            />

            <DashboardNavbar />

            <Box
                dir="rtl"
                sx={{
                    py: 3,
                    px: {
                        xs: 0,
                        md: 0.5,
                    },
                    background:
                        "linear-gradient(180deg, rgba(248,250,252,0.55) 0%, rgba(255,255,255,0) 100%)",
                }}
            >
                {/* Filters */}
                <Card
                    sx={{
                        ...panelCardSx,
                        p: {
                            xs: 2,
                            md: 3,
                        },
                        mb: 3,
                    }}
                >
                    <Stack spacing={2.5}>
                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            alignItems={{
                                xs: "stretch",
                                sm: "center",
                            }}
                            justifyContent="space-between"
                            spacing={1.5}
                        >
                            <Stack
                                direction="row"
                                spacing={1.2}
                                alignItems="center"
                            >
                                <Box
                                    sx={sectionIconSx(
                                        COLORS.text
                                    )}
                                >
                                    <TuneRoundedIcon />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        fontWeight={950}
                                        color={
                                            COLORS.text
                                        }
                                    >
                                        فیلتر گزارش
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color={
                                            COLORS.muted
                                        }
                                        fontWeight={
                                            600
                                        }
                                    >
                                        محدوده،
                                        بازه زمانی و
                                        بیزینس را مشخص
                                        کنید.
                                    </Typography>
                                </Box>
                            </Stack>

                            {(searchInfo.from_date ||
                                searchInfo.to_date) && (
                                <Button
                                    startIcon={
                                        <RestartAltRoundedIcon />
                                    }
                                    size="small"
                                    onClick={
                                        clearDateRange
                                    }
                                    sx={{
                                        alignSelf: {
                                            xs: "flex-start",
                                            sm: "center",
                                        },
                                        borderRadius: 2.5,
                                        fontWeight: 900,
                                        color:
                                            COLORS.danger,
                                        bgcolor:
                                            alpha(
                                                COLORS.danger,
                                                0.06
                                            ),
                                        px: 1.6,

                                        "&:hover": {
                                            bgcolor:
                                                alpha(
                                                    COLORS.danger,
                                                    0.1
                                                ),
                                        },
                                    }}
                                >
                                    پاک کردن تاریخ
                                </Button>
                            )}
                        </Stack>

                        {/* Date presets */}
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 3,
                                bgcolor: alpha(
                                    COLORS.primaryLight,
                                    0.045
                                ),
                                border:
                                    "1px solid",
                                borderColor:
                                    alpha(
                                        COLORS.primaryLight,
                                        0.14
                                    ),
                            }}
                        >
                            <Stack
                                direction={{
                                    xs: "column",
                                    md: "row",
                                }}
                                spacing={1.5}
                                alignItems={{
                                    xs: "stretch",
                                    md: "center",
                                }}
                                justifyContent="space-between"
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <Box
                                        sx={{
                                            ...sectionIconSx(
                                                COLORS.primary
                                            ),
                                            width: 38,
                                            height: 38,
                                            bgcolor:
                                                "#fff",
                                            boxShadow:
                                                "0 8px 18px rgba(2,132,199,0.08)",
                                        }}
                                    >
                                        <CalendarMonthRoundedIcon />
                                    </Box>

                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={950}
                                        color={
                                            COLORS.text
                                        }
                                    >
                                        بازه زمانی
                                    </Typography>
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                    useFlexGap
                                >
                                    {DATE_RANGE_PRESETS.map(
                                        (
                                            preset
                                        ) => {
                                            const isActive =
                                                selectedRangePreset ===
                                                preset.key;

                                            return (
                                                <Button
                                                    key={
                                                        preset.key
                                                    }
                                                    size="small"
                                                    variant={
                                                        isActive
                                                            ? "contained"
                                                            : "outlined"
                                                    }
                                                    onClick={() =>
                                                        handleDateRangePreset(
                                                            preset
                                                        )
                                                    }
                                                    sx={{
                                                        borderRadius: 2.5,
                                                        fontWeight: 900,
                                                        px: 1.6,
                                                        minWidth:
                                                            "auto",
                                                        whiteSpace:
                                                            "nowrap",
                                                        color:
                                                            isActive
                                                                ? "#fff"
                                                                : COLORS.primary,
                                                        borderColor:
                                                            alpha(
                                                                COLORS.primary,
                                                                0.3
                                                            ),
                                                        backgroundColor:
                                                            isActive
                                                                ? COLORS.primary
                                                                : "#fff",
                                                        boxShadow:
                                                            isActive
                                                                ? "0 8px 18px rgba(2,132,199,0.18)"
                                                                : "none",

                                                        "&:hover": {
                                                            color:
                                                                isActive
                                                                    ? "#fff"
                                                                    : COLORS.primaryDark,
                                                            borderColor:
                                                                COLORS.primary,
                                                            backgroundColor:
                                                                isActive
                                                                    ? COLORS.primaryDark
                                                                    : alpha(
                                                                          COLORS.primaryLight,
                                                                          0.07
                                                                      ),
                                                        },
                                                    }}
                                                >
                                                    {
                                                        preset.label
                                                    }
                                                </Button>
                                            );
                                        }
                                    )}
                                </Stack>
                            </Stack>
                        </Box>

                        {/* Main filters */}
                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                md={5}
                            >
                                <TextField
                                    fullWidth
                                    name="name"
                                    label="جستجوی بیزینس"
                                    value={
                                        searchInfo.name
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="مثلاً کافه، رستوران، فروشگاه..."
                                    sx={
                                        fieldSx
                                    }
                                    InputProps={{
                                        startAdornment:
                                            (
                                                <InputAdornment position="start">
                                                    <SearchRoundedIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                    }}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    type="number"
                                    name="radius"
                                    label="شعاع"
                                    value={
                                        searchInfo.radius
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="مثلاً 1"
                                    helperText="بر حسب کیلومتر"
                                    sx={
                                        fieldSx
                                    }
                                    InputProps={{
                                        startAdornment:
                                            (
                                                <InputAdornment position="start">
                                                    <RadarRoundedIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                    }}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                            >
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() =>
                                        fetchStats()
                                    }
                                    disabled={
                                        (!selectedBusiness &&
                                            !mapPosition) ||
                                        loadingStats
                                    }
                                    sx={
                                        primaryButtonSx
                                    }
                                >
                                    {loadingStats
                                        ? "در حال دریافت..."
                                        : "دریافت گزارش"}
                                </Button>
                            </Grid>
                        </Grid>
                    </Stack>
                </Card>

                {/* Selected business */}
                {selectedBusiness && (
                    <SelectedBusinessCard
                        business={
                            selectedBusiness
                        }
                        getLat={getLat}
                        getLong={getLong}
                        searchInfo={
                            searchInfo
                        }
                    />
                )}

                {/* Stats */}
                {stats && (
                    <Box sx={{ mb: 3 }}>
                        {selectedBusiness && (
                            <Box
                                sx={{
                                    display:
                                        "flex",
                                    justifyContent:
                                        "flex-end",
                                    mb: 1.5,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    startIcon={
                                        <DownloadRoundedIcon />
                                    }
                                    onClick={
                                        handleExportPdf
                                    }
                                    disabled={
                                        exportingPdf
                                    }
                                    sx={{
                                        ...primaryButtonSx,
                                        width:
                                            "auto",
                                        px: 2.2,
                                    }}
                                >
                                    {exportingPdf
                                        ? "در حال ساخت PDF..."
                                        : "خروجی PDF"}
                                </Button>
                            </Box>
                        )}

                        <Grid
                            container
                            spacing={2}
                        >
                            {statsItems.map(
                                (
                                    item
                                ) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        lg={2}
                                        key={
                                            item.title
                                        }
                                    >
                                        <StatsCard
                                            {...item}
                                        />
                                    </Grid>
                                )
                            )}
                        </Grid>
                    </Box>
                )}

                {/* Businesses */}
                <Card
                    sx={{
                        ...panelCardSx,
                        overflow:
                            "hidden",
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            p: {
                                xs: 2,
                                md: 3,
                            },
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.2}
                            alignItems="center"
                        >
                            <Box
                                sx={sectionIconSx()}
                            >
                                <StorefrontRoundedIcon />
                            </Box>

                            <Box>
                                <Typography
                                    variant="h6"
                                    fontWeight={950}
                                    color={
                                        COLORS.text
                                    }
                                >
                                    لیست بیزینس‌ها
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color={
                                        COLORS.muted
                                    }
                                    fontWeight={
                                        600
                                    }
                                >
                                    بیزینس
                                    موردنظر را
                                    انتخاب کنید
                                    تا گزارش بر
                                    اساس مختصات
                                    آن ساخته شود.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Divider />

                    <Box
                        sx={{
                            p: {
                                xs: 2,
                                md: 3,
                            },
                        }}
                    >
                        {business.length ===
                            0 &&
                        !loading ? (
                            <EmptyBusinessState />
                        ) : (
                            <Grid
                                container
                                spacing={2.3}
                            >
                                {business.map(
                                    (
                                        item
                                    ) => (
                                        <Grid
                                            item
                                            xs={
                                                12
                                            }
                                            sm={
                                                6
                                            }
                                            lg={
                                                4
                                            }
                                            xl={
                                                3
                                            }
                                            key={
                                                item.id
                                            }
                                        >
                                            <BusinessCard
                                                item={
                                                    item
                                                }
                                                selectedBusiness={
                                                    selectedBusiness
                                                }
                                                getLat={
                                                    getLat
                                                }
                                                getLong={
                                                    getLong
                                                }
                                                onSelect={
                                                    handleSelectBusiness
                                                }
                                            />
                                        </Grid>
                                    )
                                )}
                            </Grid>
                        )}
                    </Box>
                </Card>

                {/* Map */}
                <MapPickerCard
                    position={
                        mapPosition
                    }
                    locationSource={
                        locationSource
                    }
                    onChange={
                        handleMapPositionChange
                    }
                    onReport={() =>
                        fetchStats()
                    }
                    loadingStats={
                        loadingStats
                    }
                />
            </Box>
        </DashboardLayout>
    );
}